<script lang="ts">
	import { onMount } from 'svelte';
	import Navbar from '../components/Navbar.svelte';
	import { push } from 'svelte-spa-router';
	import { appStore } from '../stores/appStore';
	import type { AppApiClient } from '../api_contracts/app.client';
	import { getTokenStorage } from '../utils/localStorage';

	let client: AppApiClient | null = $state(null);
	let token = $state('');

	// Subscribe to store
	appStore.subscribe((state) => {
		client = state.client;
		token = state.token || getTokenStorage() || '';
	});

	let databases = $state([]);
	let loading = $state(true);
	let showDetailsModal = $state(false);
	let selectedDatabase = $state(null);
	let deletingId = $state('');

	async function fetchDatabases() {
		if (!client || !token) {
			console.log('Client or token not available');
			loading = false;
			return;
		}

		loading = true;
		try {
			const result = await client.getUserDatabases({ token });

			if (result.response.successFull && result.response.databases) {
				databases = result.response.databases;
			} else {
				console.error('Failed to fetch databases:', result.response.reason);
			}
		} catch (error) {
			console.error('Failed to fetch databases:', error);
		} finally {
			loading = false;
		}
	}

	async function viewDatabaseDetails(db: any) {
		if (!client || !token) return;

		try {
			const result = await client.getDatabase({ token, databaseId: db.id });

			if (result.response.successFull && result.response.database) {
				selectedDatabase = result.response.database;
				showDetailsModal = true;
			} else {
				alert(result.response.reason || 'Failed to fetch database details');
			}
		} catch (error) {
			console.error('Failed to fetch database details:', error);
			alert('Failed to fetch database details');
		}
	}

	async function deleteDatabase(dbId: string) {
		if (!confirm('Are you sure you want to delete this database? This cannot be undone.')) return;
		if (!client || !token) return;

		deletingId = dbId;
		try {
			const result = await client.deleteDatabase({ token, databaseId: dbId });

			if (result.response.successFull) {
				await fetchDatabases();
			} else {
				alert(result.response.reason || 'Failed to delete database');
			}
		} catch (error) {
			console.error('Failed to delete database:', error);
			alert('Failed to delete database');
		} finally {
			deletingId = '';
		}
	}

	function copyToClipboard(text: string) {
		navigator.clipboard.writeText(text);
		// Simple feedback - you could make this fancier
		const btn = event?.target as HTMLButtonElement;
		if (btn) {
			const originalText = btn.textContent;
			btn.textContent = 'Copied!';
			setTimeout(() => {
				btn.textContent = originalText;
			}, 2000);
		}
	}

	function navigateToNew() {
		push('/databases/new');
	}

	onMount(() => {
		fetchDatabases();
	});
</script>

<Navbar />

<div class="min-h-screen bg-gray-50 dark:bg-gray-900 p-6">
	<div class="max-w-7xl mx-auto">
		<div class="flex items-center justify-between mb-8">
			<div>
				<h1 class="text-3xl font-bold text-gray-900 dark:text-gray-100">Databases</h1>
				<p class="text-gray-500 dark:text-gray-400 mt-1">Manage your database instances</p>
			</div>
			<button
				onclick={navigateToNew}
				class="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition inline-flex items-center"
			>
				<svg class="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
					<path
						stroke-linecap="round"
						stroke-linejoin="round"
						stroke-width="2"
						d="M12 4v16m8-8H4"
					/>
				</svg>
				Create Database
			</button>
		</div>

		{#if loading}
			<div class="flex items-center justify-center h-64">
				<div class="flex flex-col items-center">
					<svg
						class="animate-spin h-10 w-10 text-indigo-600 mb-4"
						fill="none"
						viewBox="0 0 24 24"
					>
						<circle
							class="opacity-25"
							cx="12"
							cy="12"
							r="10"
							stroke="currentColor"
							stroke-width="4"
						/>
						<path
							class="opacity-75"
							fill="currentColor"
							d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
						/>
					</svg>
					<div class="text-gray-500 dark:text-gray-400">Loading databases...</div>
				</div>
			</div>
		{/if}

		{#if !loading && databases.length === 0}
			<div class="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-12 text-center">
				<div class="text-6xl mb-4">🗄️</div>
				<h3 class="text-xl font-semibold mb-2 text-gray-900 dark:text-gray-100">
					No databases yet
				</h3>
				<p class="text-gray-500 dark:text-gray-400 mb-6">
					Create your first database to get started
				</p>
				<button
					onclick={navigateToNew}
					class="px-6 py-3 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition inline-flex items-center"
				>
					<svg class="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
						<path
							stroke-linecap="round"
							stroke-linejoin="round"
							stroke-width="2"
							d="M12 4v16m8-8H4"
						/>
					</svg>
					Create Database
				</button>
			</div>
		{/if}

		{#if !loading && databases.length > 0}
			<div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
				{#each databases as db}
					<div
						class="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6 hover:shadow-lg transition"
					>
						<div class="flex items-start justify-between mb-4">
							<div class="flex items-center gap-3">
								<div
									class="w-12 h-12 rounded-lg bg-indigo-100 dark:bg-indigo-900/30 flex items-center justify-center text-2xl"
								>
									{#if db.dbType === 'postgres'}🐘{:else if db.dbType === 'mysql'}🐬{:else}🍃{/if}
								</div>
								<div>
									<h3 class="font-semibold text-gray-900 dark:text-gray-100">{db.name}</h3>
									<p class="text-xs text-gray-500 dark:text-gray-400 uppercase">{db.dbType}</p>
								</div>
							</div>
							<span
								class="px-2 py-1 text-xs rounded-full {db.status === 'running'
									? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400'
									: db.status === 'creating'
										? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400'
										: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400'}"
							>
								{db.status}
							</span>
						</div>
						<div class="space-y-2 mb-4">
							<div class="flex justify-between text-sm">
								<span class="text-gray-500 dark:text-gray-400">Host:</span>
								<span class="font-mono text-gray-900 dark:text-gray-100">{db.host}</span>
							</div>
							<div class="flex justify-between text-sm">
								<span class="text-gray-500 dark:text-gray-400">Port:</span>
								<span class="font-mono text-gray-900 dark:text-gray-100">{db.port || 'N/A'}</span
								>
							</div>
						</div>
						<div class="flex gap-2 pt-4 border-t border-gray-200 dark:border-gray-700">
							<button
								onclick={() => viewDatabaseDetails(db)}
								class="flex-1 px-3 py-2 text-sm bg-indigo-50 dark:bg-indigo-900/30 text-indigo-600 dark:text-indigo-400 rounded hover:bg-indigo-100 dark:hover:bg-indigo-900/50 transition"
							>
								Details
							</button>
							<button
								onclick={() => deleteDatabase(db.id)}
								disabled={deletingId === db.id}
								class="px-3 py-2 text-sm bg-red-50 dark:bg-red-900/30 text-red-600 dark:text-red-400 rounded hover:bg-red-100 dark:hover:bg-red-900/50 transition disabled:opacity-50"
							>
								{deletingId === db.id ? 'Deleting...' : 'Delete'}
							</button>
						</div>
					</div>
				{/each}
			</div>
		{/if}
	</div>
</div>

{#if showDetailsModal && selectedDatabase}
	<div
		class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
		onclick={() => (showDetailsModal = false)}
	>
		<div
			class="bg-white dark:bg-gray-800 rounded-lg p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto"
			onclick={(e) => e.stopPropagation()}
		>
			<div class="flex justify-between items-start mb-4">
				<h2 class="text-2xl font-bold text-gray-900 dark:text-gray-100">Connection Details</h2>
				<button
					onclick={() => (showDetailsModal = false)}
					class="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
				>
					<svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
						<path
							stroke-linecap="round"
							stroke-linejoin="round"
							stroke-width="2"
							d="M6 18L18 6M6 6l12 12"
						/>
					</svg>
				</button>
			</div>

			<div class="space-y-4">
				<div>
					<label class="block text-sm font-medium text-gray-500 dark:text-gray-400 mb-1"
						>Host</label
					>
					<div class="flex gap-2">
						<input
							type="text"
							value={selectedDatabase.host}
							readonly
							class="flex-1 px-3 py-2 bg-gray-50 dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg font-mono text-sm text-gray-900 dark:text-gray-100"
						/>
						<button
							onclick={() => copyToClipboard(selectedDatabase.host)}
							class="px-3 py-2 bg-gray-100 dark:bg-gray-700 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition text-gray-700 dark:text-gray-300"
							>Copy</button
						>
					</div>
				</div>

				<div>
					<label class="block text-sm font-medium text-gray-500 dark:text-gray-400 mb-1"
						>Port</label
					>
					<input
						type="text"
						value={selectedDatabase.port}
						readonly
						class="w-full px-3 py-2 bg-gray-50 dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg font-mono text-sm text-gray-900 dark:text-gray-100"
					/>
				</div>

				<div>
					<label class="block text-sm font-medium text-gray-500 dark:text-gray-400 mb-1"
						>Username</label
					>
					<div class="flex gap-2">
						<input
							type="text"
							value={selectedDatabase.username}
							readonly
							class="flex-1 px-3 py-2 bg-gray-50 dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg font-mono text-sm text-gray-900 dark:text-gray-100"
						/>
						<button
							onclick={() => copyToClipboard(selectedDatabase.username)}
							class="px-3 py-2 bg-gray-100 dark:bg-gray-700 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition text-gray-700 dark:text-gray-300"
							>Copy</button
						>
					</div>
				</div>

				<div>
					<label class="block text-sm font-medium text-gray-500 dark:text-gray-400 mb-1"
						>Password</label
					>
					<div class="flex gap-2">
						<input
							type="text"
							value={selectedDatabase.password}
							readonly
							class="flex-1 px-3 py-2 bg-gray-50 dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg font-mono text-sm text-gray-900 dark:text-gray-100"
						/>
						<button
							onclick={() => copyToClipboard(selectedDatabase.password)}
							class="px-3 py-2 bg-gray-100 dark:bg-gray-700 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition text-gray-700 dark:text-gray-300"
							>Copy</button
						>
					</div>
				</div>

				<div>
					<label class="block text-sm font-medium text-gray-500 dark:text-gray-400 mb-1"
						>Database Name</label
					>
					<div class="flex gap-2">
						<input
							type="text"
							value={selectedDatabase.databaseName}
							readonly
							class="flex-1 px-3 py-2 bg-gray-50 dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg font-mono text-sm text-gray-900 dark:text-gray-100"
						/>
						<button
							onclick={() => copyToClipboard(selectedDatabase.databaseName)}
							class="px-3 py-2 bg-gray-100 dark:bg-gray-700 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition text-gray-700 dark:text-gray-300"
							>Copy</button
						>
					</div>
				</div>

				<div>
					<label class="block text-sm font-medium text-gray-500 dark:text-gray-400 mb-1"
						>Connection String</label
					>
					<div class="flex gap-2">
						<textarea
							value={selectedDatabase.connectionString}
							readonly
							rows="2"
							class="flex-1 px-3 py-2 bg-gray-50 dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg font-mono text-sm text-gray-900 dark:text-gray-100"
						></textarea>
						<button
							onclick={() => copyToClipboard(selectedDatabase.connectionString)}
							class="px-3 py-2 bg-gray-100 dark:bg-gray-700 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition text-gray-700 dark:text-gray-300"
							>Copy</button
						>
					</div>
				</div>

				<button
					onclick={() => (showDetailsModal = false)}
					class="w-full px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition"
					>Close</button
				>
			</div>
		</div>
	</div>
{/if}
